import { createSlice } from '@reduxjs/toolkit';
import { getShipCells, canPlaceShip, emptyPlacementBoard } from '../../placement';

const initialShips = () => [
  { name: 'Carrier', size: 4, placed: false },
  { name: 'Battleship', size: 3, placed: false },
  { name: 'Cruiser', size: 3, placed: false },
  { name: 'Submarine', size: 2, placed: false },
  { name: 'Destroyer', size: 2, placed: false },
  { name: 'Patrol Boat 1', size: 1, placed: false },
  { name: 'Patrol Boat 2', size: 1, placed: false },
];

const battleshipSlice = createSlice({
  name: 'battleship',
  initialState: {
    phase: 'placing',       // 'placing' | 'waiting' | 'playing' | 'finished'
    myBoard: null,           // 10x10
    opponentBoard: null,     // 10x10 (masked)
    currentTurn: null,
    winner: null,
    lastShot: null,
    shipsToPlace: initialShips(),
    placedShips: [],         // array of { cells: [{x,y}] }
    placementBoard: emptyPlacementBoard(), // 10x10, 1 = own ship (placing phase)
    selectedShipIdx: 0,
    placingDirection: 'horizontal',
  },
  reducers: {
    setPhase(state, action) {
      state.phase = action.payload;
    },
    setMyBoard(state, action) {
      state.myBoard = action.payload;
    },
    setOpponentBoard(state, action) {
      state.opponentBoard = action.payload;
    },
    updateBattle(state, action) {
      const { myBoard, opponentBoard, currentTurn, lastShot, winner } = action.payload;
      state.myBoard = myBoard;
      state.opponentBoard = opponentBoard;
      state.currentTurn = currentTurn;
      state.lastShot = lastShot;
      if (winner) {
        state.winner = winner;
        state.phase = 'finished';
      }
    },
    setCurrentTurn(state, action) {
      state.currentTurn = action.payload;
    },
    toggleDirection(state) {
      state.placingDirection = state.placingDirection === 'horizontal' ? 'vertical' : 'horizontal';
    },
    // Placement is decided HERE, against the latest state — never from
    // component closures. Rapid taps (or taps landing before a re-render on a
    // slow device) each process sequentially and can't place a stale ship.
    placeShipAt(state, action) {
      const { x, y } = action.payload;
      const ship = state.shipsToPlace[state.selectedShipIdx];
      if (!ship || ship.placed) return;
      const cells = getShipCells(x, y, ship.size, state.placingDirection);
      if (!canPlaceShip(state.placementBoard, cells)) return;

      for (const c of cells) state.placementBoard[c.y][c.x] = 1;
      ship.placed = true;
      state.placedShips.push({ cells });

      const next = state.shipsToPlace.findIndex((s) => !s.placed);
      if (next >= 0) state.selectedShipIdx = next;
    },
    selectShip(state, action) {
      const ship = state.shipsToPlace[action.payload];
      if (ship && !ship.placed) state.selectedShipIdx = action.payload;
    },
    resetBS(state) {
      state.phase = 'placing';
      state.myBoard = null;
      state.opponentBoard = null;
      state.currentTurn = null;
      state.winner = null;
      state.lastShot = null;
      state.shipsToPlace = initialShips();
      state.placedShips = [];
      state.placementBoard = emptyPlacementBoard();
      state.selectedShipIdx = 0;
      state.placingDirection = 'horizontal';
    },
    restoreBS(state, action) {
      const { phase, myBoard, opponentBoard, currentTurn, winner } = action.payload;
      state.phase = phase || 'placing';
      state.myBoard = myBoard || null;
      state.opponentBoard = opponentBoard || null;
      state.currentTurn = currentTurn || null;
      state.winner = winner || null;
      state.lastShot = null;
    },
  },
});

export const {
  setPhase, setMyBoard, setOpponentBoard, updateBattle,
  setCurrentTurn, toggleDirection, placeShipAt, selectShip, resetBS, restoreBS,
} = battleshipSlice.actions;
export default battleshipSlice.reducer;
